/**
 * Adaptive pipeline activity — the EVENT MODEL, not a stepper.
 *
 * Nothing renders these stages any more. The named-stage list they used to
 * feed ("Reading the question ✓", "Searching sources 1m27s") read as
 * machinery and went stale every time the engine changed its pipeline, so
 * the transcript now shows the engine's own interim narration plus one
 * generic working line (`surface/WorkingIndicator`). What survives here is
 * the bookkeeping the run still needs: which stages have been seen, whether
 * one failed, and the counts a stage carried.
 *
 * Nothing is assumed ahead of time either: a stage exists only once its
 * first event arrives, because which stages a run performs depends on the
 * intent and on what the run actually did — the engine's R1 dropped the
 * orchestrator stage and R2 dropped the per-source evaluation stage, and a
 * client that had them hardcoded was simply wrong about both.
 */

export type ActivityStatus = 'active' | 'done' | 'failed';

/** One source's retrieval progress, accumulated across its events. */
export interface RetrievalSource {
  sourceType: string;
  documentCount: number;
  connectorsSearched: string[];
  evidenceIncluded: number | null;
  citationsPreview: CitationPreview[];
  /** The last message the engine sent about this source. */
  message: string;
  error?: string;
}

export interface CitationPreview {
  title: string;
  snippet: string;
  fileName: string;
  provider: string;
}

/**
 * A stage the pipeline actually reported.
 *
 * `key` is the raw event suffix (`retrieval`, `synthesis`, …) so a stage the
 * engine adds later renders with a derived label instead of being dropped.
 */
export interface ActivityStage {
  key: string;
  /** Human label for the key. Kept for logs and tests; not rendered. */
  label: string;
  status: ActivityStatus;
  /** Latest detail line from the engine, verbatim. */
  detail?: string;
  /** Retrieval only: per-source rows with live counts. */
  sources?: RetrievalSource[];
  /** Set on the stage that failed. */
  error?: string;
  startedAt: number;
}

/** Labels for the stages we know. Anything else is title-cased from its key. */
const STAGE_LABELS: Record<string, string> = {
  started: 'Starting',
  intent: 'Reading the question',
  orchestration: 'Choosing sources',
  retrieval: 'Searching sources',
  evaluation: 'Weighing findings',
  synthesis: 'Writing the answer',
  verification: 'Checking citations',
  completed: 'Done',
};

/** The event suffix → a human label, never blank for an unknown stage. */
export function stageLabel(key: string): string {
  return (
    STAGE_LABELS[key] ||
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/** `research.retrieval` → `retrieval`. Returns '' for a non-research event. */
export function stageKeyOf(eventName: string): string {
  return eventName.startsWith('research.') ? eventName.slice('research.'.length) : '';
}

/** Stages that are bookkeeping, not something to show as a step. */
const HIDDEN_STAGES = new Set(['completed', 'failed', 'started']);

export function isVisibleStage(key: string): boolean {
  return !!key && !HIDDEN_STAGES.has(key);
}

/* ------------------------------------------------------------------ */
/*  Reducing events into stages                                        */
/* ------------------------------------------------------------------ */

function toCitationPreview(raw: any): CitationPreview {
  return {
    title: String(raw?.title ?? ''),
    snippet: String(raw?.snippet ?? raw?.content ?? '').slice(0, 200),
    fileName: String(raw?.file_name ?? ''),
    provider: String(raw?.provider ?? ''),
  };
}

/** Merge one retrieval event into the accumulated per-source list. */
export function mergeRetrievalSource(
  sources: RetrievalSource[],
  payload: Record<string, any>,
  message: string
): RetrievalSource[] {
  const sourceType = String(payload.source_type || '').trim();
  // "Retrieval complete from N sources" has no source_type — it is a stage
  // detail, not a row. Dropping it here keeps the rows one-per-source.
  if (!sourceType) return sources;

  const incoming: RetrievalSource = {
    sourceType,
    documentCount: Number(payload.document_count ?? 0) || 0,
    connectorsSearched: Array.isArray(payload.connectors_searched)
      ? payload.connectors_searched.map(String)
      : [],
    evidenceIncluded:
      payload.evidence_included === undefined || payload.evidence_included === null
        ? null
        : Number(payload.evidence_included),
    citationsPreview: Array.isArray(payload.citations_preview)
      ? payload.citations_preview.map(toCitationPreview)
      : [],
    message,
    ...(payload.error ? { error: String(payload.error) } : {}),
  };

  const idx = sources.findIndex((s) => s.sourceType === sourceType);
  if (idx === -1) return [...sources, incoming];

  const next = [...sources];
  const existing = next[idx];
  next[idx] = {
    ...existing,
    ...incoming,
    // Counts only grow as more of a source reports in.
    documentCount: Math.max(existing.documentCount, incoming.documentCount),
    connectorsSearched: incoming.connectorsSearched.length
      ? incoming.connectorsSearched
      : existing.connectorsSearched,
    citationsPreview: incoming.citationsPreview.length
      ? incoming.citationsPreview
      : existing.citationsPreview,
    error: incoming.error || existing.error,
  };
  return next;
}

/**
 * Fold one event into the stage list.
 *
 * Appends a stage the first time its key is seen and closes the previous one;
 * later events for the same key update it in place. Order is arrival order —
 * the pipeline decides the shape of its own progress.
 */
export function reduceStage(
  stages: ActivityStage[],
  key: string,
  payload: Record<string, any>,
  message: string,
  now: number
): ActivityStage[] {
  if (!isVisibleStage(key)) return stages;

  const idx = stages.findIndex((s) => s.key === key);

  if (idx === -1) {
    // A new stage closes whatever was running — the pipeline is sequential.
    const closed = stages.map((s) =>
      s.status === 'active' ? { ...s, status: 'done' as ActivityStatus } : s
    );
    const stage: ActivityStage = {
      key,
      label: stageLabel(key),
      status: 'active',
      startedAt: now,
      ...(message ? { detail: message } : {}),
      ...(key === 'retrieval'
        ? { sources: mergeRetrievalSource([], payload, message) }
        : {}),
      ...(payload.error ? { error: String(payload.error) } : {}),
    };
    return [...closed, stage];
  }

  const next = [...stages];
  const current = next[idx];
  next[idx] = {
    ...current,
    ...(message ? { detail: message } : {}),
    ...(key === 'retrieval'
      ? { sources: mergeRetrievalSource(current.sources || [], payload, message) }
      : {}),
    ...(payload.error ? { error: String(payload.error) } : {}),
  };
  return next;
}

/** Close every open stage — the run finished. */
export function closeStages(stages: ActivityStage[]): ActivityStage[] {
  return stages.map((s) =>
    s.status === 'active' ? { ...s, status: 'done' as ActivityStatus } : s
  );
}

/** Mark the open stage failed and leave the completed ones alone. */
export function failStages(stages: ActivityStage[], error: string): ActivityStage[] {
  const hasActive = stages.some((s) => s.status === 'active');
  if (!hasActive) {
    return [
      ...stages,
      { key: 'failed', label: 'Failed', status: 'failed', error, startedAt: 0 },
    ];
  }
  return stages.map((s) =>
    s.status === 'active' ? { ...s, status: 'failed' as ActivityStatus, error } : s
  );
}

/** Total documents seen across every source in a retrieval stage. */
export function totalDocuments(stage: ActivityStage): number {
  return (stage.sources || []).reduce((sum, s) => sum + s.documentCount, 0);
}
